/**
 * Цвета раздела «График выхода» — ЕДИНСТВЕННОЕ место, где решается, какого цвета кассир. Служба цвета не хранит:
 * цвет выводится детерминированно из идентификатора (Guid), поэтому у одного и того же кассира он один и тот же на любом
 * экране, на любом устройстве и после обновления страницы.
 *
 * Точка интеграции с будущей сущностью кассира: когда у кассира появится собственный цвет (в службе кассиров), подменяется
 * только `cashierColor` — экраны берут цвет отсюда и больше нигде не считают его сами.
 *
 * Чистые функции: часы, случайные числа и DOM не читаются.
 */

/**
 * Цвет блока с именем кассира: мягкая пастель, отдельная пара для светлой и для тёмной темы. Закрашивается блок с именем
 * (таблетка, метка) и клетка таблицы, в которой кассир выходит, а не весь день. Контраст текста на заливке не ниже 4,5:1 в обеих темах.
 */
export interface Swatch {
  /** Заливка светлой темы `#rrggbb`: светлая пастель. */
  background: string
  /** Текст на ней `#rrggbb`: тёмный. */
  foreground: string
  /** Заливка тёмной темы `#rrggbb`: приглушённый тёмный оттенок того же тона. */
  darkBackground: string
  /** Текст на ней `#rrggbb`: светлый. */
  darkForeground: string
}

/** Минимальный контраст обычного текста (WCAG AA). */
export const MIN_TEXT_CONTRAST = 4.5
/** Сколько базовых оттенков у кассиров и сколько вариантов светлоты у каждого. */
export const CASHIER_HUE_COUNT = 12
/** Светлоты пастели светлой темы (высокие) и приглушённых оттенков тёмной (низкие); насыщенность умеренная: оттенки различимы, но цвет остаётся мягким. */
export const CASHIER_LIGHTNESS: readonly number[] = [83, 88, 92]
const DARK_LIGHTNESS: readonly number[] = [22, 27, 32]
const CASHIER_SATURATION = 52
const DARK_SATURATION = 28

/** Текст на светлой пастели: тёмный, не чисто чёрный. */
const TEXT_ON_PASTEL = '#1f2328'
/** Текст на приглушённой тёмной заливке: светлый, не чисто белый. */
const TEXT_ON_MUTED_DARK = '#ece8df'

/** 32-битный хэш строки (FNV-1a с финальным перемешиванием); соль отделяет палитру кассиров от других. */
function hashOf(id: string, salt: string): number {
  let hash = 0x811c9dc5
  for (const symbol of `${salt}:${id.trim().toLowerCase()}`) {
    hash ^= symbol.codePointAt(0) ?? 0
    hash = Math.imul(hash, 0x01000193)
  }
  hash ^= hash >>> 16
  hash = Math.imul(hash, 0x85ebca6b)
  hash ^= hash >>> 13
  hash = Math.imul(hash, 0xc2b2ae35)
  hash ^= hash >>> 16
  return hash >>> 0
}

function hexByte(value: number): string {
  return Math.round(Math.min(Math.max(value, 0), 1) * 255)
    .toString(16)
    .padStart(2, '0')
}

/** HSL (оттенок в градусах, насыщенность и светлота в процентах) → `#rrggbb`. */
export function hslToHex(hue: number, saturation: number, lightness: number): string {
  const s = saturation / 100
  const l = lightness / 100
  const chroma = (1 - Math.abs(2 * l - 1)) * s
  const sector = (((hue % 360) + 360) % 360) / 60
  const second = chroma * (1 - Math.abs((sector % 2) - 1))
  const rgb: readonly [number, number, number] =
    sector < 1 ? [chroma, second, 0] : sector < 2 ? [second, chroma, 0] : sector < 3 ? [0, chroma, second] : sector < 4 ? [0, second, chroma] : sector < 5 ? [second, 0, chroma] : [chroma, 0, second]
  const shift = l - chroma / 2
  return `#${hexByte(rgb[0] + shift)}${hexByte(rgb[1] + shift)}${hexByte(rgb[2] + shift)}`
}

function luminance(hex: string): number {
  const channel = (offset: number): number => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5)
}

/** Контраст двух цветов `#rrggbb` по WCAG: от 1 (одинаковые) до 21 (чёрное на белом). */
export function contrastRatio(first: string, second: string): number {
  const left = luminance(first)
  const right = luminance(second)
  return (Math.max(left, right) + 0.05) / (Math.min(left, right) + 0.05)
}


/**
 * Цвет кассира: один из 12 базовых оттенков через 30° (зелёный, синий, жёлтый и т. д. заметно различаются, соседние розовые не сливаются) × 3 светлоты (36 цветов) мягкой пастели, выбранный хэшем идентификатора. Идентификатор сравнивается
 * без учёта регистра и пробелов по краям.
 */
export function cashierColor(cashierId: string): Swatch {
  const hash = hashOf(cashierId, 'cashier')
  const hue = (hash % CASHIER_HUE_COUNT) * (360 / CASHIER_HUE_COUNT)
  const step = Math.floor(hash / CASHIER_HUE_COUNT) % CASHIER_LIGHTNESS.length
  return {
    background: hslToHex(hue, CASHIER_SATURATION, CASHIER_LIGHTNESS[step] ?? 87),
    foreground: TEXT_ON_PASTEL,
    darkBackground: hslToHex(hue, DARK_SATURATION, DARK_LIGHTNESS[step] ?? 26),
    darkForeground: TEXT_ON_MUTED_DARK,
  }
}
